import type { MoneyLimit, StaffMemberStatus } from "@wallyo/contracts/console";
import { ConsoleApiError } from "../../api/errors";
import { describeError } from "../../auth/error-message";
import { t, type MessageId } from "../../i18n";
import { formatDate, formatMoneyExact, formatTime } from "../../i18n/format";

export { categoryName, featureName, permissionName } from "../../i18n";
export { formatDateTime } from "../../i18n/format";

export const STATUS_PILL: Record<StaffMemberStatus, { label: MessageId; tone: string }> = {
  active: { label: "team.status.active", tone: "ok" },
  invited: { label: "team.status.invited", tone: "info" },
  deactivated: { label: "team.status.deactivated", tone: "mute" },
};

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** "aujourd'hui à 09:12", "hier à 18:40", "le 12 sept. 2026", or never, in the staff member's language. */
export function formatLastSeen(iso: string | null, now: Date = new Date()): string {
  if (!iso) return t("team.lastSeen.never");
  const when = new Date(iso);
  const time = formatTime(when);
  if (sameDay(when, now)) return t("team.lastSeen.today", { time });
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(when, yesterday)) return t("team.lastSeen.yesterday", { time });
  return t("team.lastSeen.date", {
    date: formatDate(when, { day: "numeric", month: "short", year: "numeric" }),
  });
}

export function countLabel(count: number, one: MessageId, many: MessageId): string {
  return count === 1 ? t(one) : t(many, { count });
}

/**
 * What a permission's limits allow, in the staff member's language:
 * "1 000 000 GNF · 15 000 XOF", or "no limit" when there are none (ADR-0019).
 */
export function formatLimits(limits: readonly MoneyLimit[]): string {
  if (limits.length === 0) return t("team.permission.noLimit");
  return limits.map((limit) => formatMoneyExact(limit.amount, limit.currency)).join(" · ");
}

export type WriteFailure = {
  message: string;
  reference: string;
  /** `stale_version`: someone changed it meanwhile. Offer a reload. */
  stale: boolean;
};

/**
 * The server's `reason` for each refusal the grid has its own words for
 * (ADR-0019). Anything else falls back to the server's message, as before.
 */
const REFUSALS: Readonly<Record<string, MessageId>> = {
  sensitivity_not_allowed: "team.refused.sensitivity",
  critical_requires_owner: "team.refused.critical",
  limit_above_own: "team.refused.limitAboveOwn",
  feature_not_granted: "team.refused.featureFirst",
  not_limited: "team.refused.notLimited",
  limit_without_grant: "team.refused.limitWithoutGrant",
  own_role: "team.refused.ownRole",
};

/**
 * How a failed write reads (ADR-0016 §2.5). A refusal by the rules
 * (`feature_not_enabled`) or a validation error shows the server's own
 * message, which says which rule refused; `stale_version` says someone
 * changed it meanwhile. Every failure carries its request ID.
 */
export function describeWriteFailure(error: unknown): WriteFailure {
  const { message, reference } = describeError(error);
  if (!(error instanceof ConsoleApiError)) return { message, reference, stale: false };
  if (error.code === "stale_version") return { message: t("team.stale"), reference, stale: true };
  const refusal = error.reason ? REFUSALS[error.reason] : undefined;
  if (refusal) return { message: t(refusal), reference, stale: false };
  if ((error.code === "feature_not_enabled" || error.code === "validation_error") && error.message) {
    return { message: t("team.refused", { message: error.message }), reference, stale: false };
  }
  return { message, reference, stale: false };
}
